import { useId } from 'react';
import clsx from 'clsx';
import { niceMax } from '@/lib/perf';

export interface TimeSeries {
  label: string;
  /** Tailwind text colour class; the line uses currentColor. */
  color: string;
  values: (number | null)[];
  dashed?: boolean;
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

/**
 * Lines over time (one point per 5 s bucket), with a y axis, minute marks and an optional goal line. Inline SVG, theme
 * colours, a text summary for screen readers.
 */
export function TimeChart({
  title,
  series,
  stepSeconds = 5,
  format,
  goal,
  height = 180,
  shade,
}: {
  title: string;
  series: TimeSeries[];
  stepSeconds?: number;
  format: (v: number) => string;
  /** A dashed line at the goal (e.g. the p95 target). */
  goal?: { value: number; label: string };
  height?: number;
  /** Shade a time range (seconds from the first point), e.g. the steady window. */
  shade?: { from: number; to: number; label: string } | null;
}) {
  const id = useId();
  const width = 640;
  const pad = { l: 56, r: 12, t: 12, b: 24 };
  const n = Math.max(...series.map((s) => s.values.length), 0);
  const nums = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  // The data sets the scale; a goal far above it (500 ms against 10 ms) would flatten every line, so it is then only
  // named in the legend.
  const dataMax = Math.max(...nums, 0);
  const goalShown = !!goal && (dataMax === 0 || goal.value <= dataMax * 3);
  const max = niceMax(Math.max(goalShown ? goal!.value : 0, dataMax, 0) * 1.05);
  const iw = width - pad.l - pad.r;
  const ih = height - pad.t - pad.b;
  const x = (i: number) => pad.l + (n > 1 ? (i / (n - 1)) * iw : iw / 2);
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const totalS = Math.max(0, (n - 1) * stepSeconds);
  const minuteEvery = totalS > 1800 ? 600 : totalS > 600 ? 300 : totalS > 120 ? 60 : 15;
  const marks = n > 1 ? Array.from({ length: Math.floor(totalS / minuteEvery) + 1 }, (_, k) => k * minuteEvery) : [];

  const path = (values: (number | null)[]) => {
    let d = '';
    let pen = false;
    values.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };
  const last = (s: TimeSeries) => [...s.values].reverse().find((v): v is number => v !== null);
  const summary = series.map((s) => {
    const vals = s.values.filter((v): v is number => v !== null);
    return vals.length ? `${s.label}: from ${format(vals[0]!)} to ${format(vals[vals.length - 1]!)}, highest ${format(Math.max(...vals))}` : `${s.label}: no data`;
  });

  return (
    <figure className="min-w-0">
      <figcaption className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-secondary">
        <span className="font-semibold text-fg">{title}</span>
        {series.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={clsx('inline-block h-0.5 w-4 rounded', s.color)} style={{ background: 'currentColor' }} aria-hidden />
            {s.label}
            {last(s) !== undefined && <span className="font-medium text-fg tabular-nums">{format(last(s)!)}</span>}
          </span>
        ))}
        {goal && (
          <span className="inline-flex items-center gap-1.5">
            {goalShown && <span className="inline-block h-0 w-4 border-t border-dashed border-danger-text" aria-hidden />}
            {goalShown ? goal.label : `${goal.label} (far above the chart)`}
          </span>
        )}
      </figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-labelledby={`${id}-desc`}>
        <desc id={`${id}-desc`}>{`${title}. ${summary.join('. ')}.`}</desc>
        {shade && n > 1 && (
          <rect
            x={x(Math.min(n - 1, shade.from / stepSeconds))}
            y={pad.t}
            width={Math.max(0, x(Math.min(n - 1, shade.to / stepSeconds)) - x(Math.min(n - 1, shade.from / stepSeconds)))}
            height={ih}
            className="fill-primary-subtle"
            opacity={0.5}
          >
            <title>{shade.label}</title>
          </rect>
        )}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} className="stroke-border-subtle" strokeWidth={1} />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" className="fill-fg-muted text-[10px] tabular-nums">
              {format(t)}
            </text>
          </g>
        ))}
        {marks.map((s) => (
          <text key={s} x={x(s / stepSeconds)} y={height - 6} textAnchor="middle" className="fill-fg-muted text-[10px] tabular-nums">
            {mmss(s)}
          </text>
        ))}
        {goal && goalShown && <line x1={pad.l} x2={width - pad.r} y1={y(goal.value)} y2={y(goal.value)} className="stroke-danger-text" strokeDasharray="4 3" strokeWidth={1} />}
        {series.map((s) => (
          <path key={s.label} d={path(s.values)} fill="none" stroke="currentColor" strokeWidth={1.75} strokeDasharray={s.dashed ? '4 3' : undefined} strokeLinejoin="round" className={s.color} />
        ))}
        {n === 0 && (
          <text x={width / 2} y={height / 2} textAnchor="middle" className="fill-fg-muted text-xs">
            No data yet
          </text>
        )}
      </svg>
    </figure>
  );
}
